/**
 * RequestQueue class to manage API rate limiting on the client side.
 * Enforces a minimum delay between requests to prevent hitting the backend/Gemini rate limits.
 * This allows the backend to be stateless (serverless-ready).
 */
class RequestQueue {
    constructor(delayMs = 4000) {
        this.queue = [];
        this.processing = false;
        this.delayMs = delayMs;
        this.lastProcessTime = 0;
    }

    /**
     * Add a request to the queue.
     * @param {Function} requestFn - Function that returns a Promise (the API call).
     * @returns {Promise} - Resolves with the result of requestFn.
     */
    add(requestFn) {
        return new Promise((resolve, reject) => {
            this.queue.push({ requestFn, resolve, reject });
            this.process();
        });
    }

    async process() {
        if (this.processing) return;
        if (this.queue.length === 0) return;

        this.processing = true;

        while (this.queue.length > 0) {
            const now = Date.now();
            const timeSinceLast = now - this.lastProcessTime;
            const waitTime = Math.max(0, this.delayMs - timeSinceLast);

            if (waitTime > 0) {
                // console.log(`[Frontend Queue] Waiting ${waitTime}ms...`);
                await new Promise((resolve) => setTimeout(resolve, waitTime));
            }

            const { requestFn, resolve, reject } = this.queue.shift();

            try {
                // console.log(`[Frontend Queue] Processing request. Remaining: ${this.queue.length}`);
                const result = await requestFn();
                this.lastProcessTime = Date.now();
                resolve(result);
            } catch (error) {
                console.error("[Frontend Queue] Request failed:", error);
                reject(error);
            }
        }

        this.processing = false;
    }
}

// Export a singleton instance
export const requestQueue = new RequestQueue();
